'use client';

var _excluded = ["className", "node", "onImageDownload", "onPreviewOpen"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import { UploadcareImage } from '@prezly/uploadcare';
import { useMeasure } from '@react-hookz/web';
import classNames from 'classnames';
import { useMemo } from 'react';
import { Lightbox } from "../../components/index.mjs";
import { DEFAULT_GALLERY_WIDTH_SSR, IMAGE_PADDING, IMAGE_SIZE } from "./constants.mjs";
import { GalleryImage } from "./GalleryImage.mjs";
import { calculateLayout, useLightboxNavigation } from "./lib/index.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function Gallery(_ref) {
  var _lightbox$image, _lightbox$image2;
  var {
      className,
      node,
      onImageDownload,
      onPreviewOpen
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  var [rect, ref] = useMeasure();
  var galleryWidth = (rect === null || rect === void 0 ? void 0 : rect.width) || DEFAULT_GALLERY_WIDTH_SSR[node.layout];
  var margin = IMAGE_PADDING[node.padding];
  var idealHeight = IMAGE_SIZE[node.thumbnail_size] + 2 * margin;
  var originalImages = useMemo(() => extractImages(node), [node]);
  var calculatedLayout = calculateLayout({
    idealHeight,
    images: originalImages,
    viewportWidth: galleryWidth
  });
  var previewImages = calculatedLayout.flatMap(row => row.map(_ref2 => {
    var {
      index,
      width,
      height
    } = _ref2;
    return originalImages[index].preview(ceil(width * 2, 500), ceil(height * 2, 500));
  }));
  var lightbox = useLightboxNavigation(originalImages);
  return /*#__PURE__*/_jsxs("figure", _objectSpread(_objectSpread({
    id: "gallery-".concat(node.uuid),
    className: classNames('prezly-slate-gallery', className, {
      'prezly-slate-gallery--contained': node.layout === 'contained',
      'prezly-slate-gallery--expanded': node.layout === 'expanded',
      'prezly-slate-gallery--full-width': node.layout === 'full-width'
    })
  }, props), {}, {
    children: [/*#__PURE__*/_jsx("div", {
      className: "prezly-slate-gallery__images",
      ref: ref,
      style: {
        margin: -margin
      },
      children: calculatedLayout.map((tiles, index) => /*#__PURE__*/_jsx(Row, {
        originalImages: originalImages,
        previewImages: previewImages,
        tiles: tiles,
        margin: margin,
        onClick: lightbox.onOpen
      }, index))
    }), /*#__PURE__*/_jsx(Lightbox, {
      image: lightbox.image,
      isNextEnabled: lightbox.hasNext,
      isPreviousEnabled: lightbox.hasPrevious,
      onClose: lightbox.onClose,
      onDownload: onImageDownload,
      onNext: lightbox.onNext,
      onOpen: onPreviewOpen,
      onPrevious: lightbox.onPrevious,
      title: (_lightbox$image = lightbox.image) === null || _lightbox$image === void 0 ? void 0 : _lightbox$image.caption,
      children: (_lightbox$image2 = lightbox.image) === null || _lightbox$image2 === void 0 ? void 0 : _lightbox$image2.caption
    })]
  }));
}
function Row(props) {
  var {
    originalImages,
    previewImages,
    tiles,
    margin,
    onClick
  } = props;
  if (tiles.length === 0) {
    return null;
  }
  var tilesWidth = tiles.map(_ref3 => {
    var {
      width
    } = _ref3;
    return width;
  }).reduce((sum, width) => sum + width, 0);
  return /*#__PURE__*/_jsx("div", {
    className: "prezly-slate-gallery__row",
    children: tiles.map(_ref4 => {
      var {
        height,
        index,
        width
      } = _ref4;
      return /*#__PURE__*/_jsx(GalleryImage, {
        originalImage: originalImages[index],
        previewImage: previewImages[index],
        onClick: onClick,
        rounded: margin > 0,
        style: {
          flexBasis: "".concat(100 * width / tilesWidth, "%"),
          width: "".concat(100 * width / tilesWidth, "%"),
          height: "".concat(100 * height / tilesWidth, "%"),
          margin
        }
      }, originalImages[index].uuid);
    })
  });
}
function extractImages(node) {
  return node.images.map(_ref5 => {
    var {
      caption,
      file
    } = _ref5;
    return UploadcareImage.createFromPrezlyStoragePayload(file, caption);
  });
}

/**
 * Round the number with the given base.
 *
 * - ceil(0.5) === 1
 * - ceil(15, 100) === 100
 * - ceil(1250, 1000) === 2000
 */
function ceil(number) {
  var base = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
  return Math.ceil(number / base) * base;
}